import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import {
  BusinessOutlined,
  Groups2Outlined,
  InfoOutlined,
  NotesOutlined,
  PersonOutlineOutlined,
  ToggleOnOutlined,
} from "@mui/icons-material";
import { useFetch } from "services/hooks/useFetch";
import {
  checkViewerPermission,
  getAdministrationRedirectPath,
  isProvisioningLoaded,
} from "services/utils/HelperFunction";
import { scrollTop } from "services/constants";
import { tooltipStyleProps } from "services/constants/Styles";
import {
  APP_BAR_OFFSET,
  designColors,
  designFontFamily,
  designShell,
} from "services/constants/designTokens";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { FilterProvider } from "pages/FilterContext";
import { Loader } from "components/Loader";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { ApproveTable } from "pages/TeamZone/Approve/approveUi";
import { paginationFooter, roleAndGroupUi, statusChip } from "../ui";
import { useListToolbar, useMeasuredHeight } from "../useListToolbar";
import {
  CompanyIcon,
  GroupIcon,
  getGroupStatus,
  groupAssignmentsByCompany,
  groupTypography,
  roleNames,
} from "./groupUtils";

const VISIBLE_COMPANIES = 2;

// Columns for the table and for the shared list toolbar (saved views,
// search, download, sort, filter). The storage key and default view id match
// the previous list so users' existing saved views still apply. Each key
// holds a plain string/number on the row so CustomFilter can search, sort,
// filter and export it; the table renders a richer cell per key.
const GROUP_HEADERS = [
  { name: "Group Name", key: "name", type: "string", icon: <Groups2Outlined fontSize="small" /> },
  { name: "Description", key: "description", type: "string", icon: <NotesOutlined fontSize="small" /> },
  {
    name: "Access Assignments",
    key: "company_names",
    type: "string",
    icon: <BusinessOutlined fontSize="small" />,
  },
  { name: "Users", key: "user_count", type: "number", icon: <PersonOutlineOutlined fontSize="small" /> },
  { name: "Status", key: "status_label", type: "autocomplete", icon: <ToggleOnOutlined fontSize="small" /> },
];
// Relative column weights, shared out among the columns the active view
// shows so the table is always filled.
const COLUMN_WEIGHTS = {
  name: 23,
  description: 29,
  company_names: 22,
  user_count: 9,
  status_label: 12,
};
// The list's columns before the redesign (key -> old default label), used
// to upgrade views saved back then -- see useListToolbar.
const LEGACY_GROUP_HEADERS = {
  name: "Group Name",
  company_names: "Companies",
  role_names: "Assigned Roles",
  department_names: "Department",
  user_count: "Users",
  status_label: "Status",
};
const USER_GROUP_LIST_DEFAULT_VIEW_ID = "__default_user_group_list_view__";

const CompanyLine = ({ company }) => (
  <Mui.Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
    <CompanyIcon size={26} />
    <Mui.Box sx={{ minWidth: 0 }}>
      <Mui.Typography sx={{ ...groupTypography.cellStrong, fontSize: 13.5, lineHeight: 1.3 }}>
        {company.company_name}
      </Mui.Typography>
      <Mui.Typography sx={{ ...groupTypography.cellMuted, lineHeight: 1.35 }}>
        {roleNames(company) || "No role assigned"}
      </Mui.Typography>
    </Mui.Box>
  </Mui.Stack>
);

const AccessAssignments = ({ assignments }) => {
  const [anchor, setAnchor] = useState(null);
  const companies = groupAssignmentsByCompany(assignments);
  if (!companies.length) {
    return <Mui.Typography sx={groupTypography.cellMuted}>No companies assigned</Mui.Typography>;
  }
  const shown = companies.slice(0, VISIBLE_COMPANIES);
  const hidden = companies.slice(VISIBLE_COMPANIES);
  return (
    <Mui.Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
      <Mui.Stack spacing={1} sx={{ minWidth: 0 }}>
        {shown.map((company) => (
          <CompanyLine key={company.company_id ?? company.company_name} company={company} />
        ))}
      </Mui.Stack>
      {hidden.length ? (
        <>
          <Mui.Chip
            label={`+${hidden.length}`}
            size="small"
            clickable
            aria-label={`Show ${hidden.length} more companies`}
            onClick={(event) => {
              event.stopPropagation();
              setAnchor(event.currentTarget);
            }}
            sx={{
              flexShrink: 0,
              height: 28,
              fontWeight: 700,
              fontFamily: designFontFamily,
              color: "#2563EB",
              backgroundColor: "#EFF6FF",
              border: "1px solid #BFDBFE",
            }}
          />
          <Mui.Popover
            open={Boolean(anchor)}
            anchorEl={anchor}
            onClose={(event) => {
              event?.stopPropagation?.();
              setAnchor(null);
            }}
            onClick={(event) => event.stopPropagation()}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            PaperProps={{
              sx: {
                p: 2,
                width: 280,
                borderRadius: "10px",
                border: `1px solid ${designColors.slate200}`,
                boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
              },
            }}
          >
            <Mui.Typography sx={{ ...groupTypography.cellStrong, mb: 1.5 }}>
              Additional Companies ({hidden.length})
            </Mui.Typography>
            <Mui.Stack spacing={1.5}>
              {hidden.map((company) => (
                <CompanyLine key={company.company_id ?? company.company_name} company={company} />
              ))}
            </Mui.Stack>
          </Mui.Popover>
        </>
      ) : null}
    </Mui.Stack>
  );
};

// refreshSignal: bumped by the parent after a create/edit save. The list
// refetches in place, keeping its rows (and filters/page) on screen with no
// full-page loader (2026-10-01, user request -- it used to remount).
export const UserGroup = ({ onViewGroup, activeGroupId, refreshSignal = 0 }) => {
  const { domain, userDetails, userScreenAccess } = useSelector((state) => state.tokenReducer);
  const history = useHistory();
  const hasViewAccess = checkViewerPermission(
    userScreenAccess?.module_details,
    "Roles & User Group",
    "User Group",
  );
  const provisioningLoaded = isProvisioningLoaded(userScreenAccess, "Administration");

  // No per-route guard exists, so a refresh on this tab without User Group
  // view access sends the user to a screen they can see instead.
  useEffect(() => {
    if (!provisioningLoaded || hasViewAccess) return;
    history.replace(getAdministrationRedirectPath(userScreenAccess?.module_details));
  }, [provisioningLoaded, hasViewAccess, userScreenAccess, history]);

  // The url is withheld until access is confirmed so a viewer without
  // access never triggers a permission-denied toast.
  const { result: groups, loading, setLoad } = useFetch(
    provisioningLoaded && hasViewAccess ? `${domain}user-group/details/` : null,
  );
  // The full-page Loader is for the first load only; later refetches keep
  // the current rows visible until the new ones arrive.
  const [loadedOnce, setLoadedOnce] = useState(false);
  useEffect(() => {
    if (!loading) setLoadedOnce(true);
  }, [loading]);
  useEffect(() => {
    if (refreshSignal) setLoad(true);
    // setLoad is a stable state setter from useFetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // null until CustomFilter reports its first filtered set, so the table
  // shows the fetched rows immediately instead of flashing "no results".
  const [toolbarRows, setToolbarRows] = useState(null);
  const tableContainerRef = useRef(null);
  const [filterBarRef, filterBarHeight] = useMeasuredHeight();
  const [footerRef, footerHeight] = useMeasuredHeight();

  const { titleOverride, visibleHeaders, subModuleId } = useListToolbar({
    headers: GROUP_HEADERS,
    defaultViewId: USER_GROUP_LIST_DEFAULT_VIEW_ID,
    storageKey: "user_group_saved_views",
    domain,
    userDetails,
    userScreenAccess,
    subModuleName: "User Group",
    screenAccess: hasViewAccess,
    onViewChange: () => setPage(0),
    legacyHeaders: LEGACY_GROUP_HEADERS,
  });
  const totalWeight = visibleHeaders.reduce(
    (sum, header) => sum + (COLUMN_WEIGHTS[header.key] ?? 15),
    0,
  );
  const columnWidth = (key) =>
    `${(((COLUMN_WEIGHTS[key] ?? 15) / totalWeight) * 100).toFixed(2)}%`;

  const rows = useMemo(
    () =>
      (Array.isArray(groups) ? groups : []).map((group) => {
        const status = getGroupStatus(group);
        return {
          ...group,
          // "Company: Role · Role; ..." so search, filter and export cover
          // both the companies and the roles in the Access Assignments column.
          company_names: groupAssignmentsByCompany(group?.assignments)
            .map((company) => `${company.company_name}: ${roleNames(company) || "-"}`)
            .join("; "),
          user_count: Number(group?.user_count ?? 0),
          statusLabel: status,
          status_label: status,
        };
      }),
    [groups],
  );
  const filteredRows = toolbarRows ?? rows;

  useEffect(() => {
    setPage(0);
  }, [toolbarRows]);

  useEffect(() => {
    scrollTop(tableContainerRef);
  }, [page]);

  if (!provisioningLoaded || (loading && !loadedOnce)) return <Loader info="Loading..." />;
  if (!hasViewAccess) return <Loader info="Redirecting..." />;

  const pagedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const renderCell = (key, group) => {
    switch (key) {
      case "name":
        return (
          <Mui.Stack direction="row" spacing={1.75} alignItems="center">
            <GroupIcon group={group} />
            <Mui.Typography sx={{ ...groupTypography.cellStrong, wordBreak: "break-word" }}>
              {group?.name}
            </Mui.Typography>
          </Mui.Stack>
        );
      case "description":
        return (
          <Mui.Typography
            title={group?.description || ""}
            sx={{
              fontSize: 14,
              color: group?.description ? designColors.slate700 : designColors.slate400,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {group?.description || "-"}
          </Mui.Typography>
        );
      case "company_names":
        return <AccessAssignments assignments={group?.assignments} />;
      case "user_count":
        return group.user_count;
      case "status_label":
        return statusChip(group.statusLabel);
      default:
        return null;
    }
  };

  return (
    <FilterProvider domain={domain} userDetails={userDetails} subModuleId={subModuleId}>
    <Mui.Box sx={{ width: "100%" }}>
      <Mui.Box
        ref={filterBarRef}
        sx={{ ...roleAndGroupUi.sectionCard, ...roleAndGroupUi.stickyCustomFilterBar, mb: 2 }}
      >
        <CustomFilter
          title="user groups"
          titleOverride={titleOverride}
          filterHeaders={visibleHeaders}
          data={rows}
          filteredData={filteredRows}
          filteredDataReady={toolbarRows !== null}
          onFilteredDataChange={setToolbarRows}
          screenAccess={hasViewAccess}
          hideCount={true}
          labeledToolbar={true}
          downloadFormats={["excel", "pdf", "csv"]}
          searchPlaceholder="Search user groups..."
        />
      </Mui.Box>
      <Mui.Box sx={{ ...roleAndGroupUi.sectionCard, overflow: "clip" }}>
        <Mui.TableContainer
          ref={tableContainerRef}
          sx={{
            width: "100%",
            overflowX: { xs: "auto", lg: "visible" },
            pb: footerHeight ? `${footerHeight}px` : 0,
          }}
        >
          <ApproveTable stickyHeader sx={{ width: "100%", minWidth: 980, tableLayout: "fixed" }}>
            <Mui.TableHead sx={designShell.tableStickyHead}>
              <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                {visibleHeaders.map((header) => (
                  <Mui.TableCell
                    key={header.key}
                    sx={{
                      ...roleAndGroupUi.tableHeadCell,
                      top: APP_BAR_OFFSET + filterBarHeight,
                      width: columnWidth(header.key),
                      py: 1.4,
                      fontWeight: 700,
                    }}
                  >
                    <Mui.Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      justifyContent={header.key === "user_count" ? "center" : "flex-start"}
                    >
                      <span>{header.name}</span>
                      {header.key === "company_names" ? (
                        <Mui.Tooltip
                          arrow
                          slotProps={tooltipStyleProps}
                          title="Companies this group can access and the role(s) it holds in each."
                        >
                          <InfoOutlined sx={{ fontSize: 16, color: designColors.slate600 }} />
                        </Mui.Tooltip>
                      ) : null}
                    </Mui.Stack>
                  </Mui.TableCell>
                ))}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {filteredRows.length === 0 ? (
                <TableEmptyState dataLength={rows} colSpan={visibleHeaders.length} />
              ) : (
                pagedRows.map((group) => {
                  const cellSx = {
                    ...roleAndGroupUi.tableBodyCell,
                    verticalAlign: "middle",
                  };
                  return (
                    <Mui.TableRow
                      key={group?.id}
                      hover
                      selected={activeGroupId === group?.id}
                      onClick={() => onViewGroup(group)}
                      sx={{
                        cursor: "pointer",
                        "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: "#ECFAF6" },
                        "&:last-child td": { borderBottom: "none" },
                      }}
                    >
                      {visibleHeaders.map((header) => (
                        <Mui.TableCell
                          key={header.key}
                          data-label={header.key === "name" ? "" : undefined}
                          sx={{
                            ...cellSx,
                            ...(header.key === "company_names" ? { py: 1.25 } : {}),
                            ...(header.key === "user_count" ? { textAlign: "center" } : {}),
                          }}
                        >
                          {renderCell(header.key, group)}
                        </Mui.TableCell>
                      ))}
                    </Mui.TableRow>
                  );
                })
              )}
            </Mui.TableBody>
          </ApproveTable>
        </Mui.TableContainer>
        <Mui.Box ref={footerRef} sx={roleAndGroupUi.stickyFooterBar}>
          {paginationFooter({
            page,
            rowsPerPage,
            total: filteredRows.length,
            itemLabel: "user groups",
            onPageChange: setPage,
            onRowsPerPageChange: (event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            },
          })}
        </Mui.Box>
      </Mui.Box>
    </Mui.Box>
    </FilterProvider>
  );
};
